<template>
  <div class="flex flex-col lg:flex-row gap-6 2xl:gap-10 lg:justify-between">
    <div v-if="showMessageListModal">
      <component
        :is="AddFanToMessageListModal"
        :is-open="showMessageListModal"
        :action="'edit'"
        :selection="messageListSelection"
        @cancel="handleMessageListCancel"
        @add="handleMessageListAdd"
      />
    </div>

    <div v-if="showAddContactModal">
      <ContactFormModal
        :is-open="showAddContactModal"
        :contact-data="editContact"
        @cancel="handleAddContactModalCancel"
        @edit="handleFanEditModal"
      />
    </div>

    <!-- main stats -->
    <div
      v-paywall="{
        ...paywallOptions,
        title: 'Unlock profiles',
        description:
          'Unlock customer profiles to get an overview for each fan.',
      }"
      :class="
        paywallOptions.showPaywall
          ? 'relative max-h-[600px] overflow-hidden'
          : ''
      "
      class="main-overview relative flex-grow min-w-0 lg:basis-2/3 2xl:max-w-[921px] p-t p-4 py-6 rounded-md border border-solid border-weak bg-surface-light"
    >
      <div
        v-if="statistics.length > 0"
        class="grid relative md:grid-cols-3 gap-6 px-4 w-full"
      >
        <AM3Statistic
          v-for="(item, index) in statistics"
          :key="`stat-${index}`"
          v-tooltip.bottom="item.tooltip"
          animate
          :start-value="item.amount >= 10 ? 1 : 0"
          :heading="item.label"
          :value="item.amount"
          :symbol="item.type == 'CURRENCY' ? currencySymbol : ''"
        />
      </div>

      <h2 class="mt-6 text-lg font-bold px-4 text-primary">
        Recent activity
      </h2>

      <div
        id="overview-activity-list"
        data-test-id="overview-activity-list"
        class="mt-6 max-w-full w-full"
      >
        <AM3ActivityList
          :activities="computedActivity"
          :loading="isFetchingActivity"
          :show-more="true"
          :pending-count="customerState.pendingActivityCount"
          @show-more-click="handleShowMoreActivityClick"
        />
      </div>
    </div>
    <!-- end of main overview -->

    <div class="sidebar w-full lg:basis-1/3 2xl:max-w-[440px] shrink-0 min-w-0">
      <!-- Contact details -->
      <AM3DetailCard
        title="Contact details"
        :link="{
          text: 'Edit contact',
          customAction: true,
          onClick: openEditModal,
        }"
        data-test-id="edit-contact"
        @link-click="openEditContactModal"
      >
        <div
          v-if="customerState?.customer"
          class="space-y-4"
        >
          <!-- Name -->
          <div class="flex flex-row items-center mb-4 text-sm">
            <div class="flex-shrink basis-1/3 pr-2 text-secondary">
              Name
            </div>
            <div class="flex-1">
              {{
                customerState.customer.firstName
                  || customerState.customer.lastName
                  ? `${customerState.customer.firstName || ""} ${customerState.customer.lastName || ""}`.trim()
                  : "-"
              }}
            </div>
          </div>

          <!-- Email -->
          <div class="flex flex-row items-center mb-4 text-sm">
            <div class="flex-shrink basis-1/3 pr-2 text-secondary">
              Email
            </div>
            <div class="flex-1 flex items-center gap-3">
              <button
                v-if="customerState.customer.emailAddress"
                class="p-1 rounded hover:bg-brand-light -ml-2 px-2 transition-colors cursor-pointer flex flex-row gap-1 text-left items-center"
                @click="
                  copyToClipboard(customerState.customer.emailAddress, 'Email')
                "
              >
                <span class="inline-block">{{
                  customerState.customer.emailAddress
                }}</span>
                <AM3Icon
                  type="document-duplicate"
                  size="sm"
                />
              </button>
            </div>
          </div>

          <!-- Mobile Number -->
          <div class="flex flex-row items-center mb-4 text-sm">
            <div class="flex-shrink basis-1/3 pr-2 text-secondary">
              Mobile
            </div>
            <div class="flex-1 flex items-center gap-2">
              <button
                v-if="customerState.customer.mobileNumber"
                class="p-1 rounded hover:bg-brand-light -ml-2 px-2 transition-colors cursor-pointer flex flex-row gap-1 items-center text-left"
                @click="
                  copyToClipboard(
                    customerState.customer.mobileNumber,
                    'Mobile number',
                  )
                "
              >
                <span>{{ customerState.customer.mobileNumber || "-" }}</span>
                <AM3Icon
                  type="document-duplicate"
                  size="sm"
                />
              </button>
            </div>
          </div>

          <!-- Location -->
          <div class="flex flex-row items-center mb-4 text-sm">
            <div class="flex-shrink basis-1/3 pr-2 text-secondary">
              Location
            </div>
            <div class="flex-1">
              {{ formatLocation(customerState.customer) }}
            </div>
          </div>

          <!-- Date of Birth -->
          <div class="flex flex-row items-center mb-4 text-sm">
            <div class="flex-shrink basis-1/3 pr-2 text-secondary">
              Date of Birth
            </div>
            <div class="flex-1">
              <AM3Formatter
                v-if="customerState.customer.dob"
                :value="customerState.customer.dob"
                type="date"
              />
              <span v-else>-</span>
            </div>
          </div>

          <!-- Gender -->
          <div class="flex flex-row items-center mb-4 text-sm">
            <div class="flex-shrink basis-1/3 pr-2 text-secondary">
              Gender
            </div>
            <div class="flex-1">
              {{ customerState.customer.gender || "-" }}
            </div>
          </div>

          <!-- Last updated -->
          <div class="flex flex-row items-center mb-4 text-sm">
            <div class="flex-shrink basis-1/3 pr-2 text-secondary">
              Last updated
            </div>
            <div class="flex-1">
              <AM3Formatter
                v-if="customerState.customer.sysMtime"
                v-tooltip="formatDateTime(customerState.customer.sysMtime)"
                :value="customerState.customer.sysMtime"
                type="relative-time"
              />
              <span v-else>-</span>
            </div>
          </div>

          <!-- First seen -->
          <div class="flex flex-row items-center mb-4 text-sm">
            <div class="flex-shrink basis-1/3 pr-2 text-secondary">
              First seen
            </div>
            <div class="flex-1">
              <AM3Formatter
                v-if="customerState.customer.registrationTime"
                v-tooltip="
                  formatDateTime(customerState.customer.registrationTime)
                "
                :value="customerState.customer.registrationTime"
                type="relative-time"
              />
              <span v-else>-</span>
            </div>
          </div>
        </div>
        <div
          v-else
          class="py-4"
        >
          <div class="flex flex-col gap-4 skeleton skeleton-lines-9">
            <AM3LoadingContent :lines="9" />
          </div>
        </div>
      </AM3DetailCard>

      <!-- Messaging Card -->
      <MessagingCard
        v-paywall="{
          ...paywallOptions,
          title: 'Unlock messaging summaries',
          description: `Unlock messaging summaries to see each contacts's open and click rates`,
        }"
        :class="
          paywallOptions.showPaywall
            ? 'relative max-h-[600px] overflow-hidden'
            : ''
        "
        :customer-state="customerMessagingSummary"
        :customer-totals="customerMessagingTotals"
        :is-loading="isFetchingCustomerMessageSummary"
        :is-loading-totals="isFetchingCustomerMessageTotals"
        :email-subscribed="
          customerState.customer?.additionalInfo?.subscriptions?.email
        "
        :sms-subscribed="
          customerState.customer?.additionalInfo?.subscriptions?.sms
        "
        @manage-subscriptions="handleManageListSubscriptions"
      />

      <!-- Customer Lists Card -->
      <AM3DetailCard
        v-paywall="{
          ...paywallOptions,
          title: 'Unlock contact lists',
          description: `Unlock lists to see which message lists this contact is subscribed to`,
        }"
        :class="
          paywallOptions.showPaywall
            ? 'relative max-h-[600px] overflow-hidden'
            : ''
        "
        class="mt-4"
        title="Lists"
        :link="{
          text: 'Manage list subscriptions',
          customAction: true,
          onClick: handleManageListSubscriptions,
        }"
        :data-test-id="'profile-lists'"
        @link-click="handleLinkClick"
      >
        <div
          v-if="isLoadingLists"
          class="py-4"
        >
          <div class="flex flex-col gap-4">
            <AM3LoadingContent :lines="1" />
            <p class="text-sm text-secondary">
              Loading lists...
            </p>
          </div>
        </div>
        <div
          v-else-if="getListsList.length"
          class="space-y-4 min-h-6"
        >
          <AM3TagList
            :tags="getListsList"
            type="surface"
            data-test-id="customer-lists"
            :wrap-tags="true"
            truncate
          />
        </div>
        <div
          v-else-if="!customerState?.customer"
          class="py-4"
        >
          <div class="flex flex-col gap-4">
            <AM3LoadingContent :lines="1" />
          </div>
        </div>

        <div
          v-else
          class="px-4 flex flex-col items-center"
        >
          <AM3Icon
            type="list-bullet"
            size="lg"
          />

          <p class="text-xs text-secondary mt-4">
            This contact isn't on any lists yet
          </p>
        </div>
      </AM3DetailCard>

      <!-- Customer Tags Card -->
      <AM3DetailCard
        v-paywall="{
          ...paywallOptions,
          title: 'Unlock contact tags',
          description: `Unlock tags to enable powerful contact organization features`,
        }"
        :class="
          paywallOptions.showPaywall
            ? 'relative max-h-[600px] overflow-hidden'
            : ''
        "
        class="mt-4"
        title="Tags"
        :link="{
          text: 'Add tags',
          customAction: true,
          onClick: handleAddTagsClick,
        }"
        :data-test-id="'profile-tags-add'"
        @link-click="handleLinkClick"
      >
        <div
          v-if="isLoadingTags"
          class="py-4"
        >
          <div class="flex flex-col gap-4">
            <AM3LoadingContent :lines="1" />
            <p class="text-sm text-secondary">
              Loading tags...
            </p>
          </div>
        </div>
        <div
          v-else-if="customerState?.fanTags?.length"
          class="space-y-4 min-h-6"
        >
          <AM3TagList
            closable
            :tags="getTagsList"
            type="surface"
            data-test-id="customer-tags"
            :wrap-tags="true"
            truncate
            @close="handleTagRemove"
          />
        </div>
        <div
          v-else-if="!customerState?.customer"
          class="py-4"
        >
          <div class="flex flex-col gap-4">
            <AM3LoadingContent :lines="1" />
          </div>
        </div>

        <div
          v-else
          class="px-4 flex flex-col items-center"
        >
          <AM3Icon
            type="tag"
            size="lg"
          />

          <p class="text-xs text-secondary mt-4">
            This contact has no tags yet
          </p>
        </div>
      </AM3DetailCard>

      <EditFanTagsModal
        v-if="displayEditFanTagsModal"
        :is-open="displayEditFanTagsModal"
        :action="editFanTagsAction"
        :selection="tagSelection"
        @cancel="displayEditFanTagsModal = false"
        @edit="handleEditFanTagsEdit"
      />

      <div
        v-if="arEnableCustomFields"
        v-paywall="{
          ...paywallOptions,
          title: 'Unlock custom fields',
          description: `Unlock tags to enable custom field management`,
        }"
        :class="
          paywallOptions.showPaywall
            ? 'relative min-h-[300px] max-h-[600px] overflow-hidden'
            : ''
        "
        class="bg-surface-light border border-weak rounded-md mt-4"
        title="Custom fields"
      >
        <AM3CustomFieldList
          title="Custom fields"
          class="data-list-container"
          :items="customFieldData"
          :loading="isFetchingCustomerCustomFields"
          @open-editor="showCustomFieldsModal = true"
        />
      </div>

      <AM3Modal
        v-model:is-open="showCustomFieldsModal"
        header-title="Custom fields"
        width="lg"
      >
        <template #body>
          <AM3DataList
            v-model:selected="customFieldTableSelected"
            :rows="customFieldTableRows"
            row-key="oid"
            :columns="customFieldTableColumns"
            :count="customFieldTableRows.length"
          >
            <template #cell-externalFieldKey="{ row }">
              <span
                v-if="row.externalFieldKey"
                class="text-secondary font-mono text-sm"
              >{{ row.externalFieldKey }}</span>
              <span
                v-else
                class="text-disabled italic text-sm"
              >Not set</span>
            </template>
            <template #cell-value="{ row }">
              <div
                class="w-full"
                @click.stop
              >
                <span
                  v-if="row.readOnly"
                  class="text-secondary"
                >{{ formatReadOnlyValue(row, editableValues[row.oid]) }}</span>
                <AM3InputText
                  v-else-if="row.fieldType === 'text'"
                  :id="`cf-${row.oid}`"
                  :model-value="editableValues[row.oid]"
                  :placeholder="`Add ${row.name}`"
                  @update:model-value="val => editableValues[row.oid] = val"
                />
                <AM3InputNumber
                  v-else-if="row.fieldType === 'number'"
                  :id="`cf-${row.oid}`"
                  :model-value="editableValues[row.oid]"
                  :placeholder="`Add ${row.name}`"
                  @update:model-value="val => editableValues[row.oid] = val"
                />
                <AM3InputDate
                  v-else-if="row.fieldType === 'date'"
                  :model-value="editableValues[row.oid]"
                  :placeholder="`Add ${row.name}`"
                  @update:model-value="val => editableValues[row.oid] = val"
                />
                <AM3InputSelect
                  v-else-if="row.fieldType === 'dropdown-single'"
                  :model-value="Array.isArray(editableValues[row.oid]) ? (editableValues[row.oid][0] ?? null) : (editableValues[row.oid] ?? null)"
                  :options="fieldSelectOptions(row.fieldOptions)"
                  placeholder="Select one"
                  @update:model-value="val => editableValues[row.oid] = val == null ? null : [val]"
                />
                <AM3InputSelect
                  v-else-if="row.fieldType === 'dropdown-multi'"
                  :model-value="editableValues[row.oid] || []"
                  :options="fieldSelectOptions(row.fieldOptions)"
                  placeholder="Select options"
                  multiple
                  :clearable="true"
                  @update:model-value="val => editableValues[row.oid] = Array.isArray(val) ? [...val] : val"
                />
              </div>
            </template>
          </AM3DataList>
        </template>
        <template #footer>
          <AM3Button
            type="primary"
            text="Save"
            :loading="isSavingCustomFields"
            :disabled="isSavingCustomFields"
            @click="saveAllCustomFields"
          />
        </template>
      </AM3Modal>
    </div>
    <!-- end of sidebar -->
  </div>
  <!-- end of overview -->
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch, toRaw, nextTick } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { useClipboard } from '@vueuse/core'
import accounting from 'accounting'
import dayjs from 'dayjs'
import { completedStatuses } from '@audience-republic/audience-manager-shared/api/promoter-integration-task'
import MessagingCard from './MessagingCard.vue'
import { usePausableInterval } from '@/composables/usePausableInterval'
import { useNuxtApp } from '#app'
import type {
  Customer,
  CustomerCustomFields,
} from '@/api/customer-profile/types'
import type {
  PrunedCustomer,
  CustomerState,
} from '@/store/modules/customer/types'
import { getComputedActivity } from '@/utils/fan-activity'

// Component imports
import AM3TagList from '@/components/am3-generic/base/AM3TagList.vue'
import EditFanTagsModal from '@/pages/audience/components/EditFanTagsModal.vue'
import AM3CustomFieldList from '@/components/am3/simple-table/AM3CustomFieldList.vue'
import AM3ActivityList from '@/components/am3/activity-list/AM3ActivityList.vue'
import AM3EmptyState from '@/components/am3/empty-state/AM3EmptyState.vue'
import AM3Statistic from '@/components/am3/statistic/AM3Statistic.vue'
import AM3DetailCard from '@/components/am3/detail-card/AM3DetailCard.vue'
import ContactFormModal from '@/pages/audience/components/ContactFormModal.vue'
import AM3Formatter from '@/components/am3-generic/data/AM3Formatter.vue'
import { useCurrencySymbol } from '@/composables/useCurrencySymbol'

// TypeScript interfaces
interface DateRangeFilter {
  name: string;
  key: string | number;
}

interface DateRangeFilters {
  title: string;
  items: DateRangeFilter[];
}

interface Statistic {
  label: string;
  type: 'CURRENCY' | 'NUMBER';
  showDecimal?: boolean;
  amount: number;
  tooltip: string;
  clickAction: () => void;
}

interface TagSelection {
  count: number;
  fanOids: number[];
  allSelected: boolean;
  fullSelection: Array<{
    oid: number;
    name: string;
    additionalInfo: any;
  }>;
}

interface MessageListSelection {
  count: number;
  allSelected: boolean;
  fanOids: number[];
  fullSelection: Array<{
    oid: number | string;
    additionalInfo: {
      subscriptions: {
        email: boolean;
        sms: boolean;
      };
    };
  }>;
}

interface EditContact {
  oid: number;
  firstName: string;
  lastName: string;
  emailAddress: string;
  mobileNumber: string;
  streetAddress?: string;
  state?: string;
  city?: string;
  postcode?: string;
  country?: string;
  gender?: string | null;
  dob?: string;
  tags: string[];
}

interface SalesChartTitleOption {
  name: string;
  key: string;
}

// Composables
const store = useStore()
const route = useRoute()
const router = useRouter()
const { $config, $arNotification, $arStyle } = useNuxtApp()
const { copy } = useClipboard()

// Reactive state
const arEnableFanActiveMembership
  = toRaw($config).public.arEnableFanActiveMembership
const arEnableCustomFields = toRaw($config).public.arEnableCustomFields
const showEditModal = ref(false)

const selectedWidget = ref<string | null>(null)

const displayEditFanTagsModal = ref(false)
const editFanTagsAction = ref<string | null>(null)
const tagSelection = ref<TagSelection | null>(null)

const showSelectDateRangeModal = ref(false)
const showMessageListModal = ref(false)
const showAddContactModal = ref(false)
const editContact = ref<EditContact | null>(null)
const messageListSelection = ref<MessageListSelection | null>(null)
const AddFanToMessageListModal = ref<any>(null)

const salesDataOverviewPeriod = ref('all')
const salesDataOverviewIndex = ref(0)
const salesDataOverviewCustomStart = ref<string | null>(null)
const salesDataOverviewCustomEnd = ref<string | null>(null)

const salesDataChartPeriod = ref('all')
const salesDataChartIndex = ref(0)
const salesDataChartCustomStart = ref<string | null>(null)
const salesDataChartCustomEnd = ref<string | null>(null)

const salesDataChartTitleOptions = ref<SalesChartTitleOption[]>([
  { name: 'Sales by Day', key: 'day' },
  { name: 'Sales by Month', key: 'month' },
  { name: 'Sales by Year', key: 'year' },
])
const salesDataChartSelectedTitleTopic = ref('month')

const dateRangeFilters = ref<DateRangeFilters>({
  title: 'Timeframe',
  items: [
    { name: 'All time', key: 'all' },
    { name: 'Last 30 days', key: 1 },
    { name: 'Last 6 months', key: 6 },
    { name: 'Last 12 months', key: 12 },
    { name: 'Last 18 months', key: 18 },
    { name: 'Last 24 months', key: 24 },
    { name: 'Custom range', key: 'custom' },
  ],
})

const showCustomFieldsModal = ref(false)
const customFieldTableSelected = ref([])
const customerMessagingSummary = ref(null)
const customerMessagingTotals = ref<any>(null)

// Store state computed properties
const customerState = computed((): CustomerState => store.state.customerModule)
const customer = computed(
  (): PrunedCustomer | null => store.state.customerModule.customer,
)
const isFetchingCustomer = computed(
  (): boolean => store.state.customerModule.isFetchingCustomer,
)
const isFetchingActivity = computed(
  (): boolean => store.state.customerModule.isFetchingActivity,
)
const isFetchingPurchases = computed(
  (): boolean => store.state.customerModule.isFetchingPurchases,
)
const customerCustomFields = computed(
  (): CustomerCustomFields | null =>
    store.state.customerModule.customerCustomFields,
)
const isFetchingCustomerCustomFields = computed(
  (): boolean => store.state.customerModule.isFetchingCustomerCustomFields,
)
const customFields = computed(() => store.state.customFieldModule.customFields)
const isFetchingCustomerMessageSummary = computed(
  (): boolean => store.state.customerModule.isFetchingCustomerMessageSummary,
)
const isFetchingCustomerMessageTotals = computed(
  (): boolean => store.state.customerModule.isFetchingCustomerMessageTotals,
)
const isLoadingTags = computed(
  (): boolean => store.state.customerModule.isFetchingFanTags,
)
const isLoadingLists = computed(
  (): boolean => store.state.customerModule.isFetchingFanMessageListSubscriptions,
)

// Store getters
const isFeatureEnabled = computed(
  () => store.getters['authModule/isFeatureEnabled'],
)
const currencySymbol = useCurrencySymbol()
const isPermissionEnabled = (keys: string[]) =>
  store.getters['authModule/isPermissionEnabled'](keys)

// Computed properties
const isCustomerProfileFeatureEnabled = computed(() => {
  return isFeatureEnabled.value(['customer-profiles'])
})

const paywallOptions = computed(() => ({
  showPaywall: !isCustomerProfileFeatureEnabled.value,
  title: 'Unlock website activity tracking',
  description:
    'Track website activity to create segments & automations based on how people engage with your website.',
  buttonText: 'Upgrade Plan',
  buttonIcon: 'rocket',
  linkDestination: '/upgrade',
}))

const getTagsList = computed((): string[] => {
  if (!customerState.value?.fanTags) return []
  return customerState.value.fanTags.map(tag => tag.name)
})

const getListsList = computed((): string[] => {
  if (!customerState.value?.fanMessageListSubscriptions) return []
  return customerState.value.fanMessageListSubscriptions
    .map(subscription => subscription.messageList?.name)
    .filter((name): name is string => !!name)
})

const computedActivity = computed(() => {
  if (
    isCustomerProfileFeatureEnabled.value
    && customerState.value.rawFanActivity?.length > 0
  ) {
    return customerState.value.rawFanActivity.map(activity => ({
      ...activity,
      dateTime: activity.activityTime,
    }))
  }

  return getComputedActivity(
    isCustomerProfileFeatureEnabled.value,
    'all',
    customerState.value.rawFanActivity,
  )
})

const computedDateRangeFiltersOverview = computed(() => {
  if (salesDataOverviewPeriod.value === 'custom') {
    return {
      title: 'Timeframe',
      items: dateRangeFilters.value.items.map((item) => {
        if (item.key !== 'custom') return item
        if (
          !salesDataOverviewCustomStart.value
          || !salesDataOverviewCustomEnd.value
        )
          return item
        const startDate = dayjs(salesDataOverviewCustomStart.value).format(
          'DD MMM YYYY',
        )
        const endDate = dayjs(salesDataOverviewCustomEnd.value).format(
          'DD MMM YYYY',
        )
        return {
          name: `Custom range (${startDate} - ${endDate})`,
          key: 'custom',
        }
      }),
    }
  }
  return dateRangeFilters.value
})

const computedDateRangeFiltersChart = computed(() => {
  if (salesDataChartPeriod.value === 'custom') {
    return {
      title: 'Timeframe',
      items: dateRangeFilters.value.items.map((item) => {
        if (item.key !== 'custom') return item
        if (!salesDataChartCustomStart.value || !salesDataChartCustomEnd.value)
          return item
        const startDate = dayjs(salesDataChartCustomStart.value).format(
          'DD MMM YYYY',
        )
        const endDate = dayjs(salesDataChartCustomEnd.value).format(
          'DD MMM YYYY',
        )
        return {
          name: `Custom range (${startDate} - ${endDate})`,
          key: 'custom',
        }
      }),
    }
  }
  return dateRangeFilters.value
})

const statistics = computed((): Statistic[] => {
  if (!isPermissionEnabled(['view-sales'])) {
    return []
  }

  return [
    {
      label: 'Total ticket spend',
      type: 'CURRENCY',
      showDecimal: false,
      amount: (customer.value?.totalTicketSales || 0) / 100,
      tooltip:
        'The total this contact has spent across all your events. Accuracy may vary based on how ticketing platforms handle refunds, cancellations & transfers.',
      clickAction: handlePurchasesClick,
    },
    {
      label: 'Total tickets purchased',
      type: 'NUMBER',
      amount: customer.value?.totalTickets || 0,
      tooltip:
        'The total number of tickets this contact has purchased across all your events. Accuracy may vary based on how ticketing platforms handle refunds, cancellations & transfers.',
      clickAction: handlePurchasesClick,
    },
    {
      label: 'Qty of events purchased',
      type: 'NUMBER',
      amount: customer.value?.totalEventCount || 0,
      tooltip:
        'The total number of your events this contact has purchased tickets to. Accuracy may vary based on how ticketing platforms handle refunds, cancellations & transfers.',
      clickAction: handleEventsClick,
    },
  ]
})

const hasCustomFieldValue = (value: unknown) => {
  if (Array.isArray(value)) {
    return value.length > 0
  }

  return value !== null && value !== undefined && value !== ''
}

const customFieldData = computed(() => {
  return customFields.value
    .map((customField: any, index: number) => {
      let value = null
      let type = null

      if (customField.fieldType === 'text') {
        value
          = customerCustomFields.value?.customFieldString?.[customField.oid]
            || null
        type = 'String'
      }
      else if (customField.fieldType === 'number') {
        value
          = customerCustomFields.value?.customFieldInteger?.[customField.oid]
        type = 'Number'
      }
      else if (customField.fieldType === 'date') {
        type = 'Date'
        const customFieldDateValue
          = customerCustomFields.value?.customFieldDatetime?.[customField.oid]
            || null
        if (customFieldDateValue) {
          const dateReformatted = new Date(customFieldDateValue)
          value = `${dateReformatted.getFullYear()}-${(parseInt(dateReformatted.getMonth().toString()) + 1).toString().padStart(2, '0')}-${dateReformatted.getDate()}`
        }
        else {
          value = null
        }
      }
      else if (
        customField.fieldType === 'dropdown-multi'
        || customField.fieldType === 'dropdown-single'
      ) {
        value
          = customerCustomFields.value?.customFieldOptions?.[customField.oid]
            || null
        type = 'Options'
      }

      return {
        index,
        hasValue: hasCustomFieldValue(value),
        entry: generateCustomFieldEntry(customField, value, type),
      }
    })
    .sort((a, b) => {
      if (a.hasValue === b.hasValue) {
        return a.index - b.index
      }

      return a.hasValue ? -1 : 1
    })
    .map(({ entry }) => entry)
})

const customFieldTypeLabel = (fieldType: string): string => {
  const labels: Record<string, string> = {
    'text': 'Text',
    'number': 'Number',
    'date': 'Date',
    'dropdown-single': 'Dropdown',
    'dropdown-multi': 'Multi-select',
  }
  return labels[fieldType] ?? fieldType
}

const arEnableExternalFieldKey = toRaw($config).public.arEnableExternalFieldKey

const customFieldTableColumns = [
  { key: 'name', title: 'Field name', truncate: true },
  // { key: 'type', title: 'Type', width: 120 },
  ...(arEnableExternalFieldKey ? [{ key: 'externalFieldKey', title: 'External field key', truncate: true }] : []),
  { key: 'value', title: 'Value' },
]

const customFieldTableRows = computed(() => {
  // Match the sidebar order (hasValue-first) by sorting against customFieldData's index.
  const orderByOid = new Map<string, number>(
    customFieldData.value.map((entry: any, i: number) => [entry.oid, i]),
  )
  return customFields.value
    .map((field: any) => ({
      oid: field.oid,
      name: field.name,
      type: customFieldTypeLabel(field.fieldType),
      value: '',
      externalFieldKey: field.externalFieldKey ?? null,
      fieldType: field.fieldType,
      fieldOptions: field.options || [],
      readOnly: !!field.readOnly,
    }))
    .sort((a: any, b: any) =>
      (orderByOid.get(a.oid) ?? Number.MAX_SAFE_INTEGER)
      - (orderByOid.get(b.oid) ?? Number.MAX_SAFE_INTEGER),
    )
})

const editableValues = ref<Record<number, any>>({})

const primeEditableValues = () => {
  if (!customerCustomFields.value) return
  const vals: Record<number, any> = {}
  customFieldData.value.forEach((field: any) => {
    // Deep-clone so editing one field can't mutate the store's array (or any
    // sibling field that happens to share a reference via the reactive proxy).
    const saved = toRaw(field.input.savedData)
    vals[field.oid] = Array.isArray(saved) ? [...saved] : saved
  })
  editableValues.value = vals
}

// Re-prime each time the modal opens so save/close/reopen reflects persisted
// state. Avoids a TDZ on generateCustomFieldEntry that an immediate watch
// on customFieldData previously triggered during setup().
watch(showCustomFieldsModal, (open) => {
  if (open) primeEditableValues()
})

const fieldSelectOptions = (options: any[]) =>
  (options || []).map((o: any) => ({ label: o.name, value: o.value || o.name, key: o.name }))

const formatReadOnlyValue = (row: any, value: any) => {
  if (value == null || value === '' || (Array.isArray(value) && value.length === 0)) return '-'
  if (row.fieldType === 'date') return dayjs(value).format('MMM D, YYYY')
  if (row.fieldType === 'dropdown-multi') {
    return Array.isArray(value) ? value.join(', ') : String(value)
  }
  if (row.fieldType === 'dropdown-single') {
    return Array.isArray(value) ? (value[0] ?? '-') : String(value)
  }
  return String(value)
}

const customFieldValuesEqual = (a: any, b: any) => {
  const isEmpty = (v: any) =>
    v == null || v === '' || (Array.isArray(v) && v.length === 0)
  if (isEmpty(a) && isEmpty(b)) return true
  if (isEmpty(a) !== isEmpty(b)) return false
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false
    const sa = [...a].map(String).sort()
    const sb = [...b].map(String).sort()
    return sa.every((v, i) => v === sb[i])
  }
  return a === b
}

const isSavingCustomFields = ref(false)

const saveAllCustomFields = async () => {
  if (isSavingCustomFields.value) return

  const originalByOid = new Map<string, any>(
    customFieldData.value.map((entry: any) => [entry.oid, entry.input.savedData]),
  )

  const writes = customFieldTableRows.value
    .filter((row: any) => !row.readOnly)
    .filter((row: any) => !customFieldValuesEqual(editableValues.value[row.oid], originalByOid.get(row.oid)))
    .map((row: any) => saveCustomFieldData({ oid: row.oid }, editableValues.value[row.oid]))

  if (writes.length === 0) {
    showCustomFieldsModal.value = false
    return
  }

  isSavingCustomFields.value = true
  try {
    await Promise.all(writes)
    showCustomFieldsModal.value = false
  }
  catch (e) {
    console.error('Failed to save custom fields', e)
    $arNotification.push({
      type: 'error',
      message: 'There was a problem saving custom fields',
    })
  }
  finally {
    isSavingCustomFields.value = false
  }
}

// Store actions and mutations
const fetchCustomer = (payload: any) =>
  store.dispatch('customerModule/FETCH_CUSTOMER', payload)
const fetchCustomerActivity = (payload: any) =>
  store.dispatch('customerModule/FETCH_CUSTOMER_ACTIVITY', payload)
const fetchCustomerPurchases = (payload: any) =>
  store.dispatch('customerModule/FETCH_CUSTOMER_PURCHASES', payload)
const fetchCustomerLoyaltyMemberships = (payload: any) =>
  store.dispatch('customerModule/FETCH_CUSTOMER_LOYALTY_MEMBERSHIPS', payload)
const fetchCustomerTags = (payload: any) =>
  store.dispatch('customerModule/FETCH_CUSTOMER_TAGS', payload)
const fetchCustomerMessageListSubscriptions = (payload: any) =>
  store.dispatch('customerModule/FETCH_CUSTOMER_MESSAGE_LIST_SUBSCRIPTIONS', payload)
const fetchCustomerMessageSummary = (payload: any) =>
  store.dispatch('customerModule/FETCH_CUSTOMER_MESSAGE_SUMMARY', payload)
const fetchCustomerMessageTotals = (payload: any) =>
  store.dispatch('customerModule/FETCH_CUSTOMER_MESSAGE_TOTALS', payload)
const updateFanCustomFieldData = (payload: any) =>
  store.dispatch('customerModule/UPDATE_FAN_CUSTOM_FIELD_DATA', payload)
const deleteFanCustomFieldData = (payload: any) =>
  store.dispatch('customerModule/DELETE_FAN_CUSTOM_FIELD_DATA', payload)
const massEditFan = (payload: any) =>
  store.dispatch('audienceModule/MASS_EDIT_FAN', payload)

const updateCustomer = (payload: any) =>
  store.commit('customerModule/UPDATE_CUSTOMER', payload)
const removeFromCustomerTags = (payload: any) =>
  store.commit('customerModule/REMOVE_FROM_CUSTOMER_TAGS', payload)

// Methods
const copyToClipboard = (text: string, label: string) => {
  copy(text)
  $arNotification.push({
    type: 'success',
    message: `${label} copied to clipboard`,
  })
}

const formatLocation = (customer: Customer): string => {
  if (!customer) return '-'

  const addressParts: string[] = []

  if (customer.streetAddress) addressParts.push(customer.streetAddress)
  if (customer.city) addressParts.push(customer.city)
  if (customer.state) addressParts.push(customer.state)
  if (customer.postcode) addressParts.push(customer.postcode)
  if (customer.country) addressParts.push(customer.country)

  if (addressParts.length > 0) {
    return addressParts.join(', ')
  }

  return '-'
}

const openEditModal = () => {
  showEditModal.value = true
}

const openEditContactModal = () => {
  if (customerState.value?.customer) {
    const customerData = customerState.value.customer
    console.log('Customer data for edit:', customerData)

    editContact.value = {
      oid: customerData.oid,
      firstName: customerData.firstName || '',
      lastName: customerData.lastName || '',
      emailAddress: customerData.emailAddress || '',
      mobileNumber: customerData.mobileNumber || '',
      streetAddress: customerData.streetAddress,
      state: customerData.state,
      city: customerData.city,
      postcode: customerData.postcode,
      country: customerData.country,
      gender: customerData.gender || null,
      dob: customerData.dob || '',
      tags: customerState.value?.fanTags?.map(tag => tag.name) || [],
    }

    console.log('Prepared editContact data:', editContact.value)
  }
  else {
    editContact.value = null
  }

  showAddContactModal.value = true
}

const handleManageListSubscriptions = async () => {
  try {
    // Fetch fresh customer data before opening modal to ensure subscription status is current
    if (customerState.value?.customer?.oid) {
      await fetchCustomer({ fanOid: customerState.value.customer.oid })
    }

    if (!AddFanToMessageListModal.value) {
      AddFanToMessageListModal.value
        = await import('@/pages/audience/components/AddFanToMessageListModal.vue').then(
          m => m.default,
        )
    }

    messageListSelection.value = {
      count: 1,
      allSelected: false,
      mode: 'edit',
      fanOids: [parseInt(route.params.customer)],
      fullSelection: [
        {
          oid: route.params.customer as string,
          emailAddress: customerState.value?.customer?.emailAddress,
          mobileNumber: customerState.value?.customer?.mobileNumber,
          additionalInfo: {
            subscriptions: {
              email:
                customerState.value?.customer?.subscriptions?.email || false,
              sms: customerState.value?.customer?.subscriptions?.sms || false,
            },
          },
        },
      ],
    }

    showMessageListModal.value = true
  }
  catch (error) {
    console.error('Failed to load the message list modal component:', error)
  }
}

const handleMessageListCancel = () => {
  showMessageListModal.value = false

  if (customerState.value?.customer?.oid) {
    fetchCustomerMessageListSubscriptions(customerState.value.customer.oid)
  }
}

const TERMINAL_STATUSES = new Set(completedStatuses)

const waitForTasksToComplete = async (promoterOid: number, taskOids: number[]) => {
  const MAX_ATTEMPTS = 10
  const POLL_INTERVAL_MS = 1000
  const api = useNuxtApp().$api.promoterIntegrationTask

  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    try {
      const tasks = await api.fetchByOids(promoterOid, taskOids)
      if (tasks.length >= taskOids.length && tasks.every(t => TERMINAL_STATUSES.has(t.status))) {
        return
      }
    }
    catch (error) {
      console.error('Failed to poll subscription-edit task status:', error)
    }
    if (attempt < MAX_ATTEMPTS - 1) {
      await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS))
    }
  }
}

const handleMessageListAdd = async (taskOids?: number[], message?: string) => {
  showMessageListModal.value = false
  // Subscription updates are queued as background tasks, so we wait for the
  // specific task(s) to finish processing before refreshing.
  if (customerState.value?.customer?.oid) {
    const promoterOid = store.state.authModule?.account?.promoterOid

    // Backend processing of the activity row for this edit can lag well behind the task
    // completing, so show a placeholder in the activity list until it actually shows up.
    store.commit('customerModule/MARK_PENDING_ACTIVITY', taskOids?.length || 1)
    currentPollIntervalMs = ACTIVE_POLL_INTERVAL_MS
    restartActivityPoll()

    // Shown immediately, honestly reflecting that this is still in progress (loading spinner instead
    // of a checkmark) rather than claiming it's done - it isn't, until the activity poll above confirms it.
    $arNotification.push({
      type: 'loading',
      message: message || 'Updating subscription preferences.',
    })

    if (taskOids?.length && promoterOid) {
      await waitForTasksToComplete(promoterOid, taskOids)
    }
    else {
      // Fallback if no task oid was returned (e.g. deploy skew) — keep old safety margin
      await new Promise(resolve => setTimeout(resolve, 2000))
    }

    // Refresh customer data to reflect subscription changes
    await fetchCustomer({ fanOid: customerState.value.customer.oid })
    await fetchCustomerMessageListSubscriptions(customerState.value.customer.oid)

    // Activity feed itself is intentionally left to the ongoing pollCustomerActivity poll (already
    // restarted above) rather than re-fetched here - the placeholder marked above stays visible until
    // that poll detects the real row, however long the backend batch job actually takes.
  }
}

const handleLinkClick = (link: any) => {
  if (link && !link.customAction && link.path) {
    router.push(link.path)
  }
}

const navigateToActivityTab = () => {
  // Find the activity tab button and simulate a click - vue router won't actually allow for this as the URLs are too similar.
  const activityTabButton = document.querySelector(
    '[data-key="activity"]',
  ) as HTMLButtonElement
  if (activityTabButton) {
    activityTabButton.click()
  }
  else {
    // Fallback to router navigation if button not found
    router.replace(`/audience/${route.params.customer}/view#activity`)
  }
}

const handleShowMoreActivityClick = () => {
  navigateToActivityTab()
}

const handleTagRemove = async (index: number, tag: string) => {
  if (!customerState.value?.customer?.oid) return

  try {
    const tagToRemove = customerState.value.fanTags.find(t => t.name === tag)

    if (tagToRemove) {
      removeFromCustomerTags(tagToRemove.oid)

      await massEditFan({
        fanOids: [customerState.value.customer.oid],
        tags: {
          remove: [tagToRemove.name],
        },
      })

      await new Promise(resolve => setTimeout(resolve, 500))

      await fetchCustomer({ fanOid: customerState.value.customer.oid })
      await fetchCustomerTags(route.params.customer)

      $arNotification.push({
        type: 'success',
        message: `Tag "${tagToRemove.name}" removed successfully.`,
      })
    }
    else {
      console.error('Tag not found:', tag)
      $arNotification.push({ type: 'error', message: 'Tag not found.' })
    }
  }
  catch (error) {
    await fetchCustomerTags(route.params.customer)
    console.error('Error removing tag:', error)
    $arNotification.push({
      type: 'error',
      message: 'Failed to remove tag. Please try again.',
    })
  }
}

const changeUserData = async (changes: any) => {
  if (!route.params.customer) return
  try {
    await massEditFan({
      fanOids: [route.params.customer],
      editData: { ...changes },
    })
    updateCustomer(changes)
  }
  catch (e) {
    console.log(e)
    $arNotification.push({
      type: 'error',
      message: `There was a problem processing your request`,
    })
  }
}

const saveCustomFieldData = (item: any, temporaryValue: any) => {
  if (
    temporaryValue == null
    || temporaryValue === ''
    || (Array.isArray(temporaryValue) && temporaryValue.length === 0)
  ) {
    return deleteFanCustomFieldData({
      fanOid: route.params.customer,
      customFieldOid: item.oid,
    })
  }

  return updateFanCustomFieldData({
    fanOid: route.params.customer,
    customFieldOid: item.oid,
    value: temporaryValue,
  })
}

const handleDateRangeCancel = () => {
  showSelectDateRangeModal.value = false
  setSalesDataFilter('all', selectedWidget.value)
}

const handleAddContactModalCancel = () => {
  showAddContactModal.value = false
  editContact.value = null

  if (customerState.value?.customer?.oid) {
    fetchCustomer({ fanOid: customerState.value.customer.oid })
    fetchCustomerTags(route.params.customer)
    fetchCustomerActivity({
      top: 24,
      skip: 0,
      types: [
        'fan-message',
        'campaign-registration',
        'event-purchase',
        'event-attendance',
        'ecommerce-purchase',
        'subscription',
      ],
      fanOid: route.params.customer,
    })
  }
}

const handleFanEditModal = () => {
  if (customerState.value?.customer?.oid) {
    fetchCustomer({ fanOid: customerState.value.customer.oid })
    fetchCustomerTags(route.params.customer)
    fetchCustomerActivity({
      top: 24,
      skip: 0,
      types: [
        'fan-message',
        'campaign-registration',
        'event-purchase',
        'event-attendance',
        'ecommerce-purchase',
        'subscription',
      ],
      fanOid: route.params.customer,
    })
  }

  showAddContactModal.value = false
  editContact.value = null
}

const handleDateRangeSelect = ({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}) => {
  showSelectDateRangeModal.value = false
  if (selectedWidget.value === 'chart') {
    salesDataChartCustomStart.value = startDate
    salesDataChartCustomEnd.value = endDate
  }
  else {
    salesDataOverviewCustomStart.value = startDate
    salesDataOverviewCustomEnd.value = endDate
  }
}

const setSalesDataFilter = async (newPeriod: string, widget: string | null) => {
  selectedWidget.value = widget
  if (newPeriod === 'custom') {
    showSelectDateRangeModal.value = true
  }

  const newSelectIndex = dateRangeFilters.value.items.findIndex(
    item => item.key === newPeriod,
  )
  if (selectedWidget.value === 'chart') {
    salesDataChartPeriod.value = newPeriod
    salesDataChartIndex.value = newSelectIndex
  }
  else {
    salesDataOverviewPeriod.value = newPeriod
    salesDataOverviewIndex.value = newSelectIndex
  }
}

const handleSalesChartTitleTopicChange = (val: any) => {
  if (!val || !val.key) return
  salesDataChartSelectedTitleTopic.value = val.key
}

const handlePurchasesClick = () => {
  router.push({
    path: `/audience/${route.params.customer}/view/purchases`,
  })
}

const handleEventsClick = () => {
  router.push({
    path: `/audience/${route.params.customer}/view/events`,
  })
}

const handleCampaignsClick = () => {
  router.push({
    path: `/audience/${route.params.customer}/view/campaigns`,
  })
}

const handleAddTagsClick = () => {
  try {
    if (customerState.value?.customer?.oid) {
      tagSelection.value = {
        count: 1,
        fanOids: [customerState.value.customer.oid],
        allSelected: false,
        fullSelection: [
          {
            oid: customerState.value.customer.oid,
            name:
              `${customerState.value.customer.firstName || ''} ${customerState.value.customer.lastName || ''}`.trim()
              || 'Customer',
            additionalInfo: {},
          },
        ],
      }

      displayEditFanTagsModal.value = true
      editFanTagsAction.value = 'add'
    }
    else {
      console.error('No customer OID available')
      $arNotification.push({
        type: 'error',
        message: 'Customer information is not available. Please try again.',
      })
    }
  }
  catch (error) {
    console.error('Failed to open tags modal:', error)
    $arNotification.push({
      type: 'error',
      message: 'Failed to open tags modal. Please try again.',
    })
  }
}

const handleEditFanTagsEdit = async () => {
  try {
    displayEditFanTagsModal.value = false

    if (customerState.value?.customer?.oid) {
      await fetchCustomer({ fanOid: customerState.value.customer.oid })
    }

    await new Promise(resolve => setTimeout(resolve, 500))

    if (route.params.customer) {
      await fetchCustomerTags(route.params.customer)
    }
  }
  catch (error) {
    console.error('Error refreshing tags data:', error)
    $arNotification.push({
      type: 'warning',
      message:
        'Tags were updated but there was an issue refreshing the display. Please reload if you don\'t see the changes.',
    })
  }
}

const generateCustomFieldEntry = (
  selectedCustomField: any,
  value: any,
  type: string,
) => {
  let options = null
  const savedData = value

  const response: any = {
    oid: selectedCustomField?.oid,
    title: selectedCustomField?.name,
    modifiable: !selectedCustomField?.readOnly,
    outputAs: type,
    input: {
      type: type,
      name: selectedCustomField?.name,
      placeholder: selectedCustomField?.name,
      savedData,
    },
  }

  if (type === 'Options' && !!selectedCustomField) {
    if (selectedCustomField.fieldType === 'dropdown-single') {
      response['input']['type'] = 'Select'
      options = selectedCustomField.options.map((option: any) => {
        return { name: option.name }
      })
    }
    else if (selectedCustomField.fieldType === 'dropdown-multi') {
      response['input']['type'] = 'Multiselect'
      options = selectedCustomField.options.map((option: any) => {
        const isSelected = savedData
          ? savedData.indexOf(option.name) > -1
          : false
        return { name: option.name, value: option.name, selected: isSelected }
      })
    }
    response['input']['options'] = options
  }

  return response
}

// Watchers
watch(
  customer,
  async (val, oldVal) => {
    if (
      val
      && val.oid
      && !oldVal
      && !isFetchingPurchases.value
      && !isFetchingActivity.value
      && isCustomerProfileFeatureEnabled.value
    ) {
      fetchCustomerActivity({
        top: 24,
        skip: 0,
        types: [
          'fan-message',
          'campaign-registration',
          'event-purchase',
          'event-attendance',
          'ecommerce-purchase',
          'subscription',
        ],
        fanOid: route.params.customer,
      })
      fetchCustomerLoyaltyMemberships(route.params.customer)
      // Always fetch customer tags to ensure we have the latest tag data
      fetchCustomerTags(route.params.customer)
      fetchCustomerMessageListSubscriptions(route.params.customer)
    }
  },
  { immediate: true },
)

// Messaging summary + totals are fetched on mount rather than in the watcher
// above, because the watcher's `!isFetchingActivity` / `!isFetchingPurchases`
// guards racy-skip the dispatch when navigating in from the audience list.
async function loadMessagingData(fanOid: string | string[]) {
  try {
    const summary = await fetchCustomerMessageSummary(fanOid)
    customerMessagingSummary.value = summary?.[0] ?? null
  }
  catch (error) {
    console.error('Failed to fetch customer message summary:', error)
  }
  try {
    const totals = await fetchCustomerMessageTotals(fanOid)
    customerMessagingTotals.value = totals
  }
  catch (error) {
    console.error('Failed to fetch customer message totals:', error)
  }
}

watch(
  () => customerState.value.fanTags,
  (newTags, oldTags) => {
    if (!oldTags || newTags?.length !== oldTags?.length) {
      nextTick()
    }
  },
)

// The backend can take a while (background processing) to reflect an edit as new activity, so
// polling faster than that just wastes requests. Poll briskly right after a save, then back off
// exponentially while nothing new shows up, settling into a slow idle heartbeat once caught up.
const ACTIVE_POLL_INTERVAL_MS = 2_000
const IDLE_POLL_INTERVAL_MS = 15_000
const MAX_POLL_INTERVAL_MS = 30_000

let currentPollIntervalMs = ACTIVE_POLL_INTERVAL_MS

const pollCustomerActivity = async () => {
  if (!route.params.customer || !isCustomerProfileFeatureEnabled.value) return

  await fetchCustomerActivity({
    top: 24,
    skip: 0,
    types: [
      'fan-message',
      'campaign-registration',
      'event-purchase',
      'event-attendance',
      'ecommerce-purchase',
      'subscription',
    ],
    fanOid: route.params.customer,
  })

  currentPollIntervalMs = customerState.value.pendingActivityCount > 0
    ? Math.min(currentPollIntervalMs * 2, MAX_POLL_INTERVAL_MS)
    : IDLE_POLL_INTERVAL_MS
  restartActivityPoll()
}

// Poll for new activity while this tab is open and the browser tab is visible
const { restart: restartActivityPoll } = usePausableInterval(pollCustomerActivity, () => currentPollIntervalMs)

// Lifecycle
onMounted(() => {
  window.scrollTo(0, 0)

  // Fetch customer activity and tags when component mounts
  if (route.params.customer && isCustomerProfileFeatureEnabled.value) {
    pollCustomerActivity()
    fetchCustomerTags(route.params.customer)
    fetchCustomerMessageListSubscriptions(route.params.customer)
    loadMessagingData(route.params.customer)
  }
})
</script>
